import { useCallback, useEffect, useState } from "react";
import { Copy, FileIcon, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { IMAGE_SLOTS } from "@/lib/design";
import { UploadBtn } from "@/components/admin/SermonForm";

export const ImagesPanel = ({ images = {}, onPatch }) => (
  <div className="grid gap-3">
    <p className="text-xs text-[#4A5568]">Tip: you can also click any photo in the preview to replace it. The banner slideshow is managed in Settings.</p>
    {IMAGE_SLOTS.map(([k, label, def]) => (
      <div key={k} data-testid={`ve-image-${k}`} className="flex items-center gap-3 rounded-xl border border-[#14213D]/10 bg-white p-2">
        <img src={fileUrl(images[k] || def)} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
        <p className="min-w-0 flex-1 font-ui text-xs font-semibold text-[#14213D]">{label}</p>
        <UploadBtn accept="image/*" testid={`ve-image-upload-${k}`} label="Replace" onDone={(u) => onPatch({ images: { [k]: u } })} />
        {images[k] && <button type="button" data-testid={`ve-image-reset-${k}`} onClick={() => onPatch({ images: { [k]: "" } })} className="rounded-full p-1.5 text-[#5F6875] hover:bg-[#14213D]/5" aria-label="Use original"><RotateCcw className="h-3.5 w-3.5" /></button>}
      </div>
    ))}
  </div>
);

const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const FilesPanel = () => {
  const [files, setFiles] = useState([]);
  const load = useCallback(() => { api.get("/design/files").then((r) => setFiles(Array.isArray(r.data) ? r.data : [])).catch(() => setFiles([])); }, []);
  useEffect(() => { load(); }, [load]);
  const link = (f) => `${window.location.origin}${f.url}`;
  const copy = (f) => navigator.clipboard.writeText(link(f)).then(() => toast.success("Link copied")).catch(() => toast(link(f)));
  const del = async (f) => {
    if (!window.confirm(`Delete “${f.name}”? Links to it will stop working.`)) return;
    try { await api.delete(`/design/files/${f.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid gap-3">
      <p className="text-xs text-[#4A5568]">Upload any kind of file (Word, PowerPoint, PDF, Excel, ZIP, music, video…) up to 500MB. Copy the link to share it, or put it on a page or announcement.</p>
      <div><UploadBtn multiple library testid="ve-files-upload" label="Upload files" onDone={() => load()} /></div>
      {files.map((f, i) => (
        <div key={f.id} data-testid={`ve-file-${i}`} className="flex items-center gap-2 rounded-xl border border-[#14213D]/10 bg-white p-2">
          <FileIcon className="h-4 w-4 shrink-0 text-[#8A6A12]" />
          <a href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate font-ui text-xs font-semibold text-[#14213D] hover:underline">{f.name}<span className="ml-1 font-normal text-[#5F6875]">· {size(f.size)}</span></a>
          <button type="button" data-testid={`ve-file-copy-${i}`} onClick={() => copy(f)} className="rounded-full p-1.5 text-[#5F6875] hover:bg-[#14213D]/5" aria-label="Copy link"><Copy className="h-3.5 w-3.5" /></button>
          <button type="button" data-testid={`ve-file-delete-${i}`} onClick={() => del(f)} className="rounded-full p-1.5 text-[#B4442A] hover:bg-[#B4442A]/10" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {!files.length && <p data-testid="ve-files-empty" className="py-4 text-center text-xs text-[#5F6875]">No files yet.</p>}
    </div>
  );
};
